/**
 * Widget API Service
 *
 * Server-side service for widget API authentication, partner management,
 * domain validation, and audit logging.
 */

import {
  createCipheriv,
  createDecipheriv,
  createHash,
  randomBytes,
} from "crypto";

import dayjs from "@punchup/utils/dayjs";
import { logException } from "@punchup/sentry";

import { createSupabaseAdminClient } from "../../supabase/supabase";
import { Json } from "../../supabase.types";
import {
  API_KEY_PREFIX,
  API_KEY_LENGTH,
  DEFAULT_RATE_LIMIT_RPM,
  DEVELOPER_KEY_PREFIX,
  TOKEN_PREFIX,
  TOKEN_LENGTH,
  TOKEN_EXPIRY_MINUTES,
  WidgetApiPartner,
  WidgetApiPartnerDomain,
  WidgetApiAuditLogParams,
  CreatePartnerParams,
  CreatePartnerResult,
  RevokePartnerParams,
  ListPartnersParams,
  UpdatePartnerParams,
  RotateApiKeyResult,
  CreateTokenParams,
  CreateTokenResult,
  ValidateTokenResult,
  CreateSelfServePartnerParams,
  ComedianWidgetConfig,
  CreateDeveloperTokenParams,
  CreateDeveloperTokenResult,
  DeveloperToken,
  DeveloperTokenScopeRef,
} from "../../models/widget-api.types";
import { resolveScopeNames } from "./developer-token-scopes";

// =============================================================================
// API Key Generation & Hashing
// =============================================================================

/**
 * Generates a new API key with secure random bytes.
 * Format: wgt_<32 hex chars> (128 bits entropy)
 *
 * @returns Object containing the raw key, its SHA-256 hash, and the prefix for indexed lookup
 */
export function generateApiKey(keyPrefix: string = API_KEY_PREFIX): {
  key: string;
  hash: string;
  prefix: string;
} {
  // Generate 16 random bytes (128 bits entropy)
  const randomPart = randomBytes(16).toString("hex");
  const key = `${keyPrefix}${randomPart}`;

  // Hash the full key for secure storage
  const hash = hashApiKey(key);

  // Use the key prefix + first 8 hex chars for indexed lookup
  const prefix = key.substring(0, keyPrefix.length + 8);

  return { key, hash, prefix };
}

/**
 * Computes SHA-256 hash of an API key for secure storage.
 *
 * @param key - The raw API key to hash
 * @returns Hex-encoded SHA-256 hash
 */
export function hashApiKey(key: string): string {
  return createHash("sha256").update(key).digest("hex");
}

// =============================================================================
// Partner Lookup & Validation
// =============================================================================

/**
 * Looks up a partner by their API key.
 * Uses prefix for indexed lookup, then verifies full hash match.
 *
 * @param apiKey - The raw API key from the request header
 * @returns The partner if found and hash matches, null otherwise
 */
export async function getPartnerByApiKey(
  apiKey: string
): Promise<WidgetApiPartner | null> {
  // Both key generations resolve here: legacy `wgt_` keys and the neutral `pk_`
  // developer-portal keys. Which surface a partner may use is the guard's
  // decision, made on `kind`, not on the prefix.
  const keyPrefix = [API_KEY_PREFIX, DEVELOPER_KEY_PREFIX].find((p) =>
    apiKey?.startsWith(p)
  );
  if (!apiKey || !keyPrefix) {
    return null;
  }

  // Expected length: key prefix + 32 hex chars
  const expectedLength = keyPrefix.length + API_KEY_LENGTH;
  if (apiKey.length !== expectedLength) {
    return null;
  }

  // Extract prefix for indexed lookup
  const prefix = apiKey.substring(0, keyPrefix.length + 8);

  const supabase = createSupabaseAdminClient();

  // Look up by prefix (indexed for performance)
  const { data: partners, error } = await supabase
    .from("widget_api_partners")
    .select("*")
    .eq("api_key_prefix", prefix);

  if (error) {
    logException(error, {
      tags: {
        what: "Error looking up widget API partner by key prefix",
        supabase: true,
      },
    });
    return null;
  }

  if (!partners || partners.length === 0) {
    return null;
  }

  // Compute hash of the provided key
  const providedHash = hashApiKey(apiKey);

  // Find partner with matching hash (handles potential prefix collisions)
  const partner = partners.find((p) => p.api_key_hash === providedHash);

  return (partner as WidgetApiPartner) ?? null;
}

/**
 * Fetches all active domain patterns for a partner.
 *
 * @param partnerId - UUID of the partner
 * @returns Array of active domain patterns
 */
export async function getPartnerDomains(
  partnerId: string
): Promise<WidgetApiPartnerDomain[]> {
  const supabase = createSupabaseAdminClient();

  const { data: domains, error } = await supabase
    .from("widget_api_partner_domains")
    .select("*")
    .eq("partner_id", partnerId)
    .eq("is_active", true);

  if (error) {
    logException(error, {
      tags: {
        what: "Error fetching widget API partner domains",
        supabase: true,
        partnerId,
      },
    });
    return [];
  }

  return domains ?? [];
}

// =============================================================================
// Domain & Comedian Access Validation
// =============================================================================

/**
 * Checks if an origin is allowed based on the partner's configured domain patterns.
 * Supports exact matches (example.com) and wildcard patterns (*.example.com).
 *
 * Rules:
 * - Empty domain list means all domains are allowed
 * - Null/undefined origin is rejected if domains are configured
 * - Wildcards (*.example.com) match any subdomain including the root domain
 * - All comparisons are case-insensitive
 *
 * @param origin - The Origin header value from the request
 * @param allowedDomains - Array of allowed domain patterns
 * @returns True if the origin is allowed
 */
export function isDomainAllowed(
  origin: string | null,
  allowedDomains: WidgetApiPartnerDomain[]
): boolean {
  // Empty domain list = all domains allowed
  if (allowedDomains.length === 0) {
    return true;
  }

  // If domains are configured, we need an origin to validate
  if (!origin) {
    return false;
  }

  // Extract hostname from origin (handles "https://example.com" format)
  let hostname: string;
  try {
    const url = new URL(origin);
    hostname = url.hostname.toLowerCase();
  } catch {
    // If origin is just a hostname without protocol, use it directly
    hostname = origin.toLowerCase();
  }

  // Check each allowed domain pattern
  for (const domainConfig of allowedDomains) {
    const pattern = domainConfig.domain_pattern.toLowerCase();

    if (pattern.startsWith("*.")) {
      // Wildcard pattern: *.example.com
      const baseDomain = pattern.substring(2); // Remove "*."

      // Match subdomain.example.com or example.com itself
      if (hostname === baseDomain || hostname.endsWith(`.${baseDomain}`)) {
        return true;
      }
    } else {
      // Exact match
      if (hostname === pattern) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Shared allow-list check. The two axes disagree on what a null list means, so
 * the policy is an explicit argument rather than a per-function convention.
 * An empty list always denies.
 */
function isIdAllowed(
  id: string,
  list: string[] | null,
  { nullMeans }: { nullMeans: "all" | "none" }
): boolean {
  if (list === null) {
    return nullMeans === "all";
  }

  return list.includes(id);
}

/**
 * Checks if a comedian ID is in the partner's allowed list.
 * Null allowed_comedian_ids means all comedians are allowed.
 *
 * @param comedianId - The comedian UUID being queried
 * @param partner - The widget API partner
 * @returns True if the partner can access this comedian
 */
export function isComedianAllowed(
  comedianId: string,
  partner: WidgetApiPartner
): boolean {
  return isIdAllowed(comedianId, partner.allowed_comedian_ids, {
    nullMeans: "all",
  });
}

/**
 * Checks if a venue page ID is in the partner's allowed list.
 *
 * NOTE: unlike isComedianAllowed, a null list means DENY, not "all venues".
 * Venue access is exclusively for venue-scoped developer tokens (which set an
 * explicit [venuePageId] array); every other partner — admin and comedian
 * self-serve — has this brand-new column null and must NOT gain implicit
 * access to every venue's data.
 *
 * @param venuePageId - The venue page UUID being queried
 * @param partner - The widget API partner
 * @returns True if the partner is explicitly scoped to this venue
 */
export function isVenueAllowed(
  venuePageId: string,
  partner: WidgetApiPartner
): boolean {
  return isIdAllowed(venuePageId, partner.allowed_venue_page_ids, {
    nullMeans: "none",
  });
}

/**
 * Checks if a comedian ID is allowed by a token's scope.
 * If the token has null allowedComedianIds, falls back to the partner's allowed list.
 *
 * @param comedianId - The comedian UUID being queried
 * @param tokenAllowedComedianIds - The token's allowed comedian IDs (null means inherit from partner)
 * @param partner - The widget API partner
 * @returns True if the token can access this comedian
 */
export function isComedianAllowedByToken(
  comedianId: string,
  tokenAllowedComedianIds: string[] | null,
  partner: WidgetApiPartner
): boolean {
  // If token has no specific restrictions, fall back to partner's allowed list
  if (tokenAllowedComedianIds === null) {
    return isComedianAllowed(comedianId, partner);
  }

  // Empty array means no comedians allowed (edge case)
  if (tokenAllowedComedianIds.length === 0) {
    return false;
  }

  // Check if comedian is in the token's allowed list
  return tokenAllowedComedianIds.includes(comedianId);
}

// =============================================================================
// Audit Logging
// =============================================================================

/**
 * Logs a widget API audit event.
 * Non-blocking: errors are logged but do not fail the request.
 *
 * @param params - Audit log parameters
 */
export async function logAuditEvent(
  params: WidgetApiAuditLogParams
): Promise<void> {
  const supabase = createSupabaseAdminClient();

  const { error } = await supabase.from("widget_api_audit_log").insert({
    partner_id: params.partnerId ?? null,
    event_type: params.eventType,
    comedian_id: params.comedianId ?? null,
    origin: params.origin ?? null,
    ip_address: params.ipAddress ?? null,
    user_agent: params.userAgent ?? null,
    error_message: params.errorMessage ?? null,
    metadata: (params.metadata ?? {}) as Json,
  });

  if (error) {
    // Log but don't throw - audit logging should not fail requests
    logException(error, {
      tags: {
        what: "Error inserting widget API audit log",
        supabase: true,
      },
      extra: {
        eventType: params.eventType,
        partnerId: params.partnerId,
      },
    });
  }
}

// =============================================================================
// Partner CRUD Operations
// =============================================================================

/**
 * Creates a new widget API partner with generated API key.
 *
 * @param params - Partner creation parameters
 * @returns The created partner and the raw API key (shown only once!)
 */
export async function createPartner(
  params: CreatePartnerParams
): Promise<CreatePartnerResult> {
  const supabase = createSupabaseAdminClient();

  // Generate API key
  const { key, hash, prefix } = generateApiKey();

  // Insert partner record
  const { data: partner, error: partnerError } = await supabase
    .from("widget_api_partners")
    .insert({
      name: params.name,
      description: params.description ?? null,
      api_key_hash: hash,
      api_key_prefix: prefix,
      is_active: true,
      rate_limit_rpm: params.rateLimitRpm ?? 60,
      allowed_comedian_ids: params.allowedComedianIds ?? null,
      contact_email: params.contactEmail ?? null,
      contact_name: params.contactName ?? null,
      created_by: params.createdBy ?? null,
    })
    .select()
    .single();

  if (partnerError || !partner) {
    logException(partnerError, {
      tags: {
        what: "Error creating widget API partner",
        supabase: true,
      },
    });
    throw new Error(`Failed to create partner: ${partnerError?.message}`);
  }

  // Insert domain patterns if provided
  if (params.domains && params.domains.length > 0) {
    const domainRecords = params.domains.map((domain) => ({
      partner_id: partner.id,
      domain_pattern: domain.toLowerCase().trim(),
      is_active: true,
    }));

    const { error: domainError } = await supabase
      .from("widget_api_partner_domains")
      .insert(domainRecords);

    if (domainError) {
      // Partner was created, but domains failed - log but don't throw
      logException(domainError, {
        tags: {
          what: "Error inserting widget API partner domains",
          supabase: true,
          partnerId: partner.id,
        },
      });
    }
  }

  return {
    partner: partner as WidgetApiPartner,
    apiKey: key,
  };
}

/**
 * Revokes a partner's access by marking them as revoked.
 * Also deactivates all associated domains.
 *
 * @param params - Revocation parameters
 * @returns True when a row was revoked, false when none matched - an unknown
 * partnerId, or a partner the `ownedBy` caller does not own.
 */
export async function revokePartner(
  params: RevokePartnerParams
): Promise<boolean> {
  const supabase = createSupabaseAdminClient();

  const update = supabase
    .from("widget_api_partners")
    .update({
      is_active: false,
      revoked_at: dayjs().toISOString(),
      revoked_by: params.revokedBy ?? null,
      revoked_reason: params.reason,
      updated_at: dayjs().toISOString(),
    })
    .eq("id", params.partnerId);

  // When ownedBy is set, scope the write to created_by so a partner the caller
  // doesn't own matches no row and the revoke is a no-op returning false.
  if (params.ownedBy) {
    const { data: revoked, error } = await update
      .eq("created_by", params.ownedBy)
      .select("id")
      .maybeSingle();
    if (error) {
      logException(error, {
        tags: {
          what: "Error revoking widget API partner",
          supabase: true,
          partnerId: params.partnerId,
        },
      });
      throw new Error(`Failed to revoke partner: ${error.message}`);
    }
    if (!revoked) {
      return false;
    }
  } else {
    const { data: revoked, error: partnerError } = await update
      .select("id")
      .maybeSingle();
    if (partnerError) {
      logException(partnerError, {
        tags: {
          what: "Error revoking widget API partner",
          supabase: true,
          partnerId: params.partnerId,
        },
      });
      throw new Error(`Failed to revoke partner: ${partnerError.message}`);
    }
    // An unknown partnerId matches no row. Report that as false, the same as
    // the ownership-scoped branch, so the return value never claims a revoke
    // that did not happen.
    if (!revoked) {
      return false;
    }
  }

  // Also deactivate all domains for this partner
  const { error: domainError } = await supabase
    .from("widget_api_partner_domains")
    .update({ is_active: false })
    .eq("partner_id", params.partnerId);

  if (domainError) {
    logException(domainError, {
      tags: {
        what: "Error deactivating widget API partner domains on revoke",
        supabase: true,
        partnerId: params.partnerId,
      },
    });
    // Don't throw - partner is already revoked, domain deactivation is secondary
  }

  return true;
}

/**
 * Lists widget API partners.
 *
 * @param params - Optional filtering parameters
 * @returns Array of partners
 */
export async function listPartners(
  params?: ListPartnersParams
): Promise<WidgetApiPartner[]> {
  const supabase = createSupabaseAdminClient();

  let query = supabase
    .from("widget_api_partners")
    .select("*")
    .order("created_at", { ascending: false });

  // Filter out revoked partners unless explicitly requested
  if (!params?.includeRevoked) {
    query = query.eq("is_active", true);
  }

  const { data: partners, error } = await query;

  if (error) {
    logException(error, {
      tags: {
        what: "Error listing widget API partners",
        supabase: true,
      },
    });
    throw new Error(`Failed to list partners: ${error.message}`);
  }

  return (partners as WidgetApiPartner[]) ?? [];
}

/**
 * Gets a single partner by ID.
 *
 * @param partnerId - UUID of the partner
 * @returns The partner or null if not found
 */
export async function getPartnerById(
  partnerId: string
): Promise<WidgetApiPartner | null> {
  const supabase = createSupabaseAdminClient();

  const { data: partner, error } = await supabase
    .from("widget_api_partners")
    .select("*")
    .eq("id", partnerId)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      // Not found
      return null;
    }
    logException(error, {
      tags: {
        what: "Error fetching widget API partner by ID",
        supabase: true,
        partnerId,
      },
    });
    return null;
  }

  return partner as WidgetApiPartner;
}

/**
 * Updates a partner's allowed domains.
 * Replaces all existing domains with the new list.
 *
 * @param partnerId - UUID of the partner
 * @param domains - New list of allowed domain patterns
 */
export async function updatePartnerDomains(
  partnerId: string,
  domains: string[]
): Promise<void> {
  const supabase = createSupabaseAdminClient();

  // Delete existing domains
  const { error: deleteError } = await supabase
    .from("widget_api_partner_domains")
    .delete()
    .eq("partner_id", partnerId);

  if (deleteError) {
    logException(deleteError, {
      tags: {
        what: "Error deleting widget API partner domains",
        supabase: true,
        partnerId,
      },
    });
    throw new Error(`Failed to update partner domains: ${deleteError.message}`);
  }

  // Insert new domains if any provided
  if (domains.length > 0) {
    const domainRecords = domains.map((domain) => ({
      partner_id: partnerId,
      domain_pattern: domain.toLowerCase().trim(),
      is_active: true,
    }));

    const { error: insertError } = await supabase
      .from("widget_api_partner_domains")
      .insert(domainRecords);

    if (insertError) {
      logException(insertError, {
        tags: {
          what: "Error inserting widget API partner domains",
          supabase: true,
          partnerId,
        },
      });
      throw new Error(
        `Failed to update partner domains: ${insertError.message}`
      );
    }
  }
}

/**
 * Updates a partner's fields.
 * Only provided fields will be updated. Domains are handled separately via updatePartnerDomains.
 *
 * @param partnerId - UUID of the partner to update
 * @param params - Fields to update (all optional)
 * @returns The updated partner or null if not found
 */
export async function updatePartner(
  partnerId: string,
  params: UpdatePartnerParams
): Promise<WidgetApiPartner | null> {
  const supabase = createSupabaseAdminClient();

  // Build update object with only provided fields
  const updateData: Record<string, unknown> = {
    updated_at: dayjs().toISOString(),
  };

  if (params.name !== undefined) {
    updateData.name = params.name;
  }
  if (params.description !== undefined) {
    updateData.description = params.description;
  }
  if (params.contactEmail !== undefined) {
    updateData.contact_email = params.contactEmail;
  }
  if (params.contactName !== undefined) {
    updateData.contact_name = params.contactName;
  }
  if (params.allowedComedianIds !== undefined) {
    updateData.allowed_comedian_ids = params.allowedComedianIds;
  }
  if (params.rateLimitRpm !== undefined) {
    updateData.rate_limit_rpm = params.rateLimitRpm;
  }

  // Update partner record
  const { data: partner, error } = await supabase
    .from("widget_api_partners")
    .update(updateData)
    .eq("id", partnerId)
    .select()
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      // Not found
      return null;
    }
    logException(error, {
      tags: {
        what: "Error updating widget API partner",
        supabase: true,
        partnerId,
      },
    });
    throw new Error(`Failed to update partner: ${error.message}`);
  }

  // Update domains if provided
  if (params.domains !== undefined) {
    await updatePartnerDomains(partnerId, params.domains);
  }

  return partner as WidgetApiPartner;
}

/**
 * Rotates a partner's API key by generating a new one.
 * The old key will immediately stop working.
 *
 * @param partnerId - UUID of the partner
 * @returns The new raw API key (shown only once!) or null if partner not found
 */
export async function rotateApiKey(
  partnerId: string
): Promise<RotateApiKeyResult | null> {
  const supabase = createSupabaseAdminClient();

  // Generate new API key
  const { key, hash, prefix } = generateApiKey();

  // Update partner with new key hash and prefix
  const { data: partner, error } = await supabase
    .from("widget_api_partners")
    .update({
      api_key_hash: hash,
      api_key_prefix: prefix,
      updated_at: dayjs().toISOString(),
    })
    .eq("id", partnerId)
    .select()
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      // Not found
      return null;
    }
    logException(error, {
      tags: {
        what: "Error rotating widget API partner key",
        supabase: true,
        partnerId,
      },
    });
    throw new Error(`Failed to rotate API key: ${error.message}`);
  }

  if (!partner) {
    return null;
  }

  return { apiKey: key };
}

// =============================================================================
// Short-Lived Token Operations (Server-Side Mode)
// =============================================================================

/**
 * Generates a new short-lived token with secure random bytes.
 * Format: wgtk_<32 hex chars> (128 bits entropy)
 *
 * @returns Object containing the raw token and its SHA-256 hash
 */
export function generateToken(): { token: string; hash: string } {
  // Generate 16 random bytes (128 bits entropy)
  const randomPart = randomBytes(16).toString("hex");
  const token = `${TOKEN_PREFIX}${randomPart}`;

  // Hash the full token for secure storage
  const hash = hashToken(token);

  return { token, hash };
}

/**
 * Computes SHA-256 hash of a token for secure storage.
 *
 * @param token - The raw token to hash
 * @returns Hex-encoded SHA-256 hash
 */
export function hashToken(token: string): string {
  return createHash("sha256").update(token).digest("hex");
}

/**
 * Creates a short-lived token for a partner to use in client-side widget.
 * Tokens expire after TOKEN_EXPIRY_MINUTES (default: 5 minutes).
 *
 * @param params - Token creation parameters
 * @returns The raw token, expiry time, and allowed comedian IDs (token shown only once!)
 */
export async function createToken(
  params: CreateTokenParams
): Promise<CreateTokenResult> {
  const supabase = createSupabaseAdminClient();

  // Generate token
  const { token, hash } = generateToken();

  // Calculate expiry time
  const expiresAt = dayjs().add(TOKEN_EXPIRY_MINUTES, "minute").toISOString();

  // Determine allowed comedian IDs for the token
  // Empty array or undefined means inherit from partner (stored as null)
  const allowedComedianIds =
    params.allowedComedianIds && params.allowedComedianIds.length > 0
      ? params.allowedComedianIds
      : null;

  // Insert token record
  const { error } = await supabase.from("widget_api_tokens").insert({
    partner_id: params.partnerId,
    token_hash: hash,
    allowed_comedian_ids: allowedComedianIds,
    expires_at: expiresAt,
  });

  if (error) {
    logException(error, {
      tags: {
        what: "Error creating widget API token",
        supabase: true,
        partnerId: params.partnerId,
      },
    });
    throw new Error(`Failed to create token: ${error.message}`);
  }

  return { token, expiresAt, allowedComedianIds };
}

/**
 * Validates a short-lived token and returns the associated partner and allowed comedian IDs.
 * Also marks the token as used (first use timestamp).
 *
 * @param token - The raw token from the request header
 * @returns Validation result with partner and allowed comedian IDs if valid
 */
export async function validateToken(
  token: string
): Promise<ValidateTokenResult> {
  // Validate token format
  if (!token || !token.startsWith(TOKEN_PREFIX)) {
    return {
      isValid: false,
      partner: null,
      allowedComedianIds: null,
      error: "Invalid token format",
    };
  }

  // Expected length: prefix (5 chars) + 32 hex chars = 37 chars
  const expectedLength = TOKEN_PREFIX.length + TOKEN_LENGTH;
  if (token.length !== expectedLength) {
    return {
      isValid: false,
      partner: null,
      allowedComedianIds: null,
      error: "Invalid token length",
    };
  }

  const supabase = createSupabaseAdminClient();

  // Compute hash of the provided token
  const providedHash = hashToken(token);

  // Look up token by hash
  const { data: tokenRecord, error: tokenError } = await supabase
    .from("widget_api_tokens")
    .select("*")
    .eq("token_hash", providedHash)
    .single();

  if (tokenError || !tokenRecord) {
    return {
      isValid: false,
      partner: null,
      allowedComedianIds: null,
      error: "Token not found",
    };
  }

  // Check if token has expired
  if (dayjs().isAfter(dayjs(tokenRecord.expires_at))) {
    return {
      isValid: false,
      partner: null,
      allowedComedianIds: null,
      error: "Token has expired",
    };
  }

  // Get the associated partner
  const partner = await getPartnerById(tokenRecord.partner_id);
  if (!partner) {
    return {
      isValid: false,
      partner: null,
      allowedComedianIds: null,
      error: "Partner not found",
    };
  }

  // Check partner is active
  if (!partner.is_active || partner.revoked_at) {
    return {
      isValid: false,
      partner: null,
      allowedComedianIds: null,
      error: "Partner has been revoked",
    };
  }

  // Mark token as used if not already (fire and forget)
  if (!tokenRecord.used_at) {
    void (async () => {
      const { error } = await supabase
        .from("widget_api_tokens")
        .update({ used_at: dayjs().toISOString() })
        .eq("id", tokenRecord.id);

      if (error) {
        logException(error, {
          tags: {
            what: "Error marking widget API token as used",
            supabase: true,
          },
        });
      }
    })();
  }

  return {
    isValid: true,
    partner,
    allowedComedianIds: tokenRecord.allowed_comedian_ids,
  };
}

// =============================================================================
// API Key Encryption (Self-Serve Partners)
// =============================================================================

/**
 * Encrypts a raw API key using AES-256-GCM for secure storage.
 * The encrypted key can be decrypted later to display in the comedian dashboard.
 *
 * @param rawKey - The raw API key to encrypt
 * @returns Encrypted string in format: iv:authTag:ciphertext (all base64)
 */
export function encryptApiKey(rawKey: string): string {
  const secret = process.env.WIDGET_API_KEY_ENCRYPTION_SECRET;
  if (!secret) {
    throw new Error("WIDGET_API_KEY_ENCRYPTION_SECRET is not configured");
  }

  // Derive a 32-byte key from the secret
  const key = new Uint8Array(createHash("sha256").update(secret).digest());
  const iv = new Uint8Array(randomBytes(12));
  const cipher = createCipheriv("aes-256-gcm", key, iv);

  let encrypted = cipher.update(rawKey, "utf8", "base64");
  encrypted += cipher.final("base64");
  const authTag = cipher.getAuthTag();

  return `${Buffer.from(iv).toString("base64")}:${authTag.toString("base64")}:${encrypted}`;
}

/**
 * Decrypts an encrypted API key stored for self-serve partners.
 *
 * @param encryptedKey - The encrypted string in format: iv:authTag:ciphertext
 * @returns The decrypted raw API key
 */
export function decryptApiKey(encryptedKey: string): string {
  const secret = process.env.WIDGET_API_KEY_ENCRYPTION_SECRET;
  if (!secret) {
    throw new Error("WIDGET_API_KEY_ENCRYPTION_SECRET is not configured");
  }

  const [ivBase64, authTagBase64, ciphertext] = encryptedKey.split(":");
  if (!ivBase64 || !authTagBase64 || !ciphertext) {
    throw new Error("Invalid encrypted key format");
  }

  const key = new Uint8Array(createHash("sha256").update(secret).digest());
  const iv = new Uint8Array(Buffer.from(ivBase64, "base64"));
  const authTag = new Uint8Array(Buffer.from(authTagBase64, "base64"));
  const decipher = createDecipheriv("aes-256-gcm", key, iv);
  decipher.setAuthTag(authTag);

  let decrypted = decipher.update(ciphertext, "base64", "utf8");
  decrypted += decipher.final("utf8");

  return decrypted;
}

// =============================================================================
// Self-Serve Partner Operations (Comedian Dashboard)
// =============================================================================

/**
 * Looks up a self-serve partner by comedian ID.
 *
 * @param comedianId - The comedian's UUID
 * @returns The active self-serve partner, or null if none exists
 */
export async function getPartnerByComedianId(
  comedianId: string
): Promise<WidgetApiPartner | null> {
  const supabase = createSupabaseAdminClient();

  const { data: partner, error } = await supabase
    .from("widget_api_partners")
    .select("*")
    .eq("comedian_id", comedianId)
    .eq("is_active", true)
    .maybeSingle();

  if (error) {
    logException(error, {
      tags: {
        what: "Error looking up self-serve widget partner by comedian ID",
        supabase: true,
        comedianId,
      },
    });
    return null;
  }

  return partner as WidgetApiPartner | null;
}

/**
 * Creates a self-serve widget partner for a comedian.
 * The partner is scoped to only the comedian's shows with no domain restrictions.
 * If an active partner already exists, returns the existing one (idempotent).
 *
 * @param params - Self-serve partner creation parameters
 * @returns The partner config with decrypted API key
 */
export async function createSelfServePartner(
  params: CreateSelfServePartnerParams
): Promise<ComedianWidgetConfig> {
  // Check for existing active self-serve partner (idempotent).
  // Partners created via the internal tool won't have api_key_encrypted,
  // so we skip them and create a separate self-serve partner.
  const existing = await getPartnerByComedianId(params.comedianId);
  if (existing && existing.api_key_encrypted) {
    const apiKey = decryptApiKey(existing.api_key_encrypted);
    return {
      partnerId: existing.id,
      apiKey,
      apiKeyPrefix: existing.api_key_prefix,
      isActive: existing.is_active,
      createdAt: existing.created_at,
    };
  }

  const supabase = createSupabaseAdminClient();

  // Generate API key
  const { key, hash, prefix } = generateApiKey();

  // Encrypt the raw key for persistent storage
  const encryptedKey = encryptApiKey(key);

  // Insert partner record scoped to this comedian
  const { data: partner, error } = await supabase
    .from("widget_api_partners")
    .insert({
      name: `${params.comedianName} (Self-Serve)`,
      description: "Auto-created from comedian dashboard",
      api_key_hash: hash,
      api_key_prefix: prefix,
      api_key_encrypted: encryptedKey,
      is_active: true,
      rate_limit_rpm: 60,
      // Discriminator must be set explicitly — the column defaults to 'admin',
      // and the one-active-partner-per-comedian unique index is scoped to
      // kind = 'self_serve'.
      kind: "self_serve",
      allowed_comedian_ids: [params.comedianId],
      comedian_id: params.comedianId,
      created_by: params.createdBy,
    })
    .select()
    .single();

  if (error || !partner) {
    logException(error, {
      tags: {
        what: "Error creating self-serve widget partner",
        supabase: true,
        comedianId: params.comedianId,
      },
    });
    throw new Error(`Failed to create self-serve partner: ${error?.message}`);
  }

  return {
    partnerId: partner.id,
    apiKey: key,
    apiKeyPrefix: prefix,
    isActive: true,
    createdAt: partner.created_at,
  };
}

/**
 * Gets the decrypted API key for a self-serve partner.
 *
 * @param partnerId - The partner's UUID
 * @returns The decrypted raw API key
 */
export async function getSelfServeApiKey(partnerId: string): Promise<string> {
  const partner = await getPartnerById(partnerId);
  if (!partner || !partner.api_key_encrypted) {
    throw new Error("Partner not found or not a self-serve partner");
  }
  return decryptApiKey(partner.api_key_encrypted);
}

/**
 * Gets the full widget config for a comedian's self-serve partner.
 *
 * @param comedianId - The comedian's UUID
 * @returns The widget config with decrypted API key, or null if not set up
 */
export async function getComedianWidgetConfig(
  comedianId: string
): Promise<ComedianWidgetConfig | null> {
  const partner = await getPartnerByComedianId(comedianId);
  if (!partner || !partner.api_key_encrypted) {
    return null;
  }

  const apiKey = decryptApiKey(partner.api_key_encrypted);

  return {
    partnerId: partner.id,
    apiKey,
    apiKeyPrefix: partner.api_key_prefix,
    isActive: partner.is_active,
    createdAt: partner.created_at,
  };
}

/**
 * Rotates the API key for a comedian's self-serve partner.
 * The old key immediately stops working.
 *
 * @param comedianId - The comedian's UUID
 * @returns The new widget config with the new decrypted API key
 */
export async function rotateSelfServeApiKey(
  comedianId: string
): Promise<ComedianWidgetConfig> {
  const partner = await getPartnerByComedianId(comedianId);
  if (!partner) {
    throw new Error("No active self-serve partner found for this comedian");
  }

  const supabase = createSupabaseAdminClient();

  // Generate new API key
  const { key, hash, prefix } = generateApiKey();
  const encryptedKey = encryptApiKey(key);

  // Update partner with new key
  const { data: updated, error } = await supabase
    .from("widget_api_partners")
    .update({
      api_key_hash: hash,
      api_key_prefix: prefix,
      api_key_encrypted: encryptedKey,
      updated_at: dayjs().toISOString(),
    })
    .eq("id", partner.id)
    .select()
    .single();

  if (error || !updated) {
    logException(error, {
      tags: {
        what: "Error rotating self-serve widget API key",
        supabase: true,
        partnerId: partner.id,
      },
    });
    throw new Error(`Failed to rotate API key: ${error?.message}`);
  }

  return {
    partnerId: updated.id,
    apiKey: key,
    apiKeyPrefix: prefix,
    isActive: updated.is_active,
    createdAt: updated.created_at,
  };
}

/**
 * Revokes a comedian's self-serve widget partner.
 *
 * @param comedianId - The comedian's UUID
 * @param revokedBy - The user ID performing the revocation
 */
export async function revokeSelfServePartner(
  comedianId: string,
  revokedBy: string
): Promise<void> {
  const partner = await getPartnerByComedianId(comedianId);
  if (!partner) {
    throw new Error("No active self-serve partner found for this comedian");
  }

  await revokePartner({
    partnerId: partner.id,
    reason: "Disabled by comedian from dashboard",
    revokedBy,
  });
}

// =============================================================================
// Developer Token Operations (developers.punchup.live self-serve)
// =============================================================================

/** Resolved scope names for a batch of partner rows, one map per axis. */
type ScopeNameMaps = {
  comedians: Map<string, string>;
  venues: Map<string, string>;
};

/**
 * Maps an allowed_* id array to resolved scope refs, preserving the DB's
 * three-state meaning: null (all) stays null; [] (none) and [ids] (specific)
 * map through, attaching each id's display name (null when unresolved).
 */
function toScopeRefs(
  ids: string[] | null,
  names: Map<string, string>
): DeveloperTokenScopeRef[] | null {
  if (ids === null) {
    return null;
  }
  return ids.map((id) => ({ id, name: names.get(id) ?? null }));
}

/**
 * Maps a partner row to the list-view DeveloperToken shape, resolving each
 * allowed comedian/venue id to a display name from the batch-resolved maps.
 * Never exposes the key hash or the encrypted key.
 */
function toDeveloperTokenWithScopes(
  partner: WidgetApiPartner,
  names: ScopeNameMaps
): DeveloperToken {
  return {
    id: partner.id,
    name: partner.name,
    description: partner.description,
    apiKeyPrefix: partner.api_key_prefix,
    isActive: partner.is_active,
    kind: partner.kind,
    rateLimitRpm: partner.rate_limit_rpm,
    comedianScopes: toScopeRefs(partner.allowed_comedian_ids, names.comedians),
    venueScopes: toScopeRefs(partner.allowed_venue_page_ids, names.venues),
    createdAt: partner.created_at,
    revokedAt: partner.revoked_at,
  };
}

/**
 * Creates a developer token owned by a user, scoped to exactly one comedian or
 * one venue. Scope is stored as the single element of the matching allowed_*
 * array (the other array is left empty, which the guard reads as "none allowed"),
 * giving mutual exclusivity for free. Stores the raw key AES-encrypted so the
 * portal can re-display it, mirroring the self-serve model.
 *
 * @param params - Token creation parameters (including the comedian/venue scope)
 * @returns The created token (list-view shape) and the raw API key (shown only once!)
 */
export async function createDeveloperToken(
  params: CreateDeveloperTokenParams
): Promise<CreateDeveloperTokenResult> {
  const supabase = createSupabaseAdminClient();

  // Generate the key (neutral pk_ prefix - these tokens authorize the public
  // developer APIs) and encrypt it for persistent (re-displayable) storage.
  const { key, hash, prefix } = generateApiKey(DEVELOPER_KEY_PREFIX);
  const encryptedKey = encryptApiKey(key);

  // Scope columns: the chosen axis gets [id]; the other gets [] (= none allowed),
  // so a comedian token is denied at venue endpoints and vice versa.
  const scopeColumns =
    params.scope.type === "comedian"
      ? {
          allowed_comedian_ids: [params.scope.comedianId],
          allowed_venue_page_ids: [] as string[],
        }
      : {
          allowed_comedian_ids: [] as string[],
          allowed_venue_page_ids: [params.scope.venuePageId],
        };

  const { data: partner, error } = await supabase
    .from("widget_api_partners")
    .insert({
      name: params.name,
      description: params.description ?? "Created from the developer portal",
      api_key_hash: hash,
      api_key_prefix: prefix,
      api_key_encrypted: encryptedKey,
      is_active: true,
      rate_limit_rpm: DEFAULT_RATE_LIMIT_RPM,
      kind: "developer",
      created_by: params.userId,
      // Self-serve identity column is unused by developer tokens; scope lives in
      // the allowed_* arrays and ownership in created_by.
      comedian_id: null,
      ...scopeColumns,
      // Developer tokens only ever read (widget/venue shows), so mark them
      // read-only — matches the migration's intent and future-proofs a
      // read-only guard.
      read_only: true,
    })
    .select()
    .single();

  if (error || !partner) {
    logException(error, {
      tags: {
        what: "Error creating developer token",
        supabase: true,
      },
      extra: { userId: params.userId },
    });
    throw new Error(`Failed to create developer token: ${error?.message}`);
  }

  // Names aren't resolved on creation (the portal refetches the list, which
  // resolves them); the scope refs carry the ids the caller just chose.
  const emptyNames: ScopeNameMaps = { comedians: new Map(), venues: new Map() };
  return {
    token: toDeveloperTokenWithScopes(partner as WidgetApiPartner, emptyNames),
    apiKey: key,
  };
}

/**
 * Lists every token a user created, newest first, regardless of kind — the
 * portal manages all of them (developer tokens plus any self_serve token the
 * user generated from a comedian dashboard). Each row's allowed comedians/venues
 * are resolved to display names in a single batched lookup across the result set.
 * Returns list-view shapes only — never the key hash or encrypted key.
 *
 * @param userId - The owning user's UUID (auth.users.id)
 * @returns The user's tokens with resolved scope names
 */
export async function listDeveloperTokens(
  userId: string
): Promise<DeveloperToken[]> {
  const supabase = createSupabaseAdminClient();

  const { data: partners, error } = await supabase
    .from("widget_api_partners")
    .select("*")
    .eq("created_by", userId)
    .order("created_at", { ascending: false });

  if (error) {
    logException(error, {
      tags: {
        what: "Error listing developer tokens",
        supabase: true,
      },
      extra: { userId },
    });
    throw new Error(`Failed to list developer tokens: ${error.message}`);
  }

  const rows = (partners as WidgetApiPartner[]) ?? [];

  // Collect every referenced comedian/venue id across the result set, then
  // resolve all names once (deduped) rather than per-row.
  const comedianIds = [
    ...new Set(rows.flatMap((r) => r.allowed_comedian_ids ?? [])),
  ];
  const venueIds = [
    ...new Set(rows.flatMap((r) => r.allowed_venue_page_ids ?? [])),
  ];
  const names = await resolveScopeNames(supabase, { comedianIds, venueIds });

  return rows.map((row) => toDeveloperTokenWithScopes(row, names));
}

/**
 * Rotates a developer token's API key. Verifies the token is owned by the user
 * before rotating; the old key immediately stops working.
 *
 * @param tokenId - The token (partner row) UUID
 * @param userId - The owning user's UUID (auth.users.id)
 * @returns The new raw API key (shown only once!), or null if not found / not owned
 */
export async function rotateDeveloperToken(
  tokenId: string,
  userId: string
): Promise<RotateApiKeyResult | null> {
  const supabase = createSupabaseAdminClient();

  // Rotation mints on the current developer prefix, so a legacy wgt_ token
  // upgrades to pk_ on its first rotate.
  const { key, hash, prefix } = generateApiKey(DEVELOPER_KEY_PREFIX);
  const encryptedKey = encryptApiKey(key);

  // Ownership is enforced on the write (created_by), so a token the user doesn't
  // own matches no row and returns null — no separate check-then-write.
  const { data: rotated, error } = await supabase
    .from("widget_api_partners")
    .update({
      api_key_hash: hash,
      api_key_prefix: prefix,
      api_key_encrypted: encryptedKey,
      updated_at: dayjs().toISOString(),
    })
    .eq("id", tokenId)
    .eq("created_by", userId)
    .select("id")
    .maybeSingle();

  if (error) {
    logException(error, {
      tags: {
        what: "Error rotating developer token",
        supabase: true,
      },
      extra: { tokenId, userId },
    });
    throw new Error(`Failed to rotate developer token: ${error.message}`);
  }

  if (!rotated) {
    return null;
  }

  return { apiKey: key };
}

/**
 * Revokes a developer token. Verifies the token is owned by the user before
 * revoking.
 *
 * @param tokenId - The token (partner row) UUID
 * @param userId - The owning user's UUID (auth.users.id)
 * @returns True if revoked, false if not found / not owned by the user
 */
export async function revokeDeveloperToken(
  tokenId: string,
  userId: string
): Promise<boolean> {
  return revokePartner({
    partnerId: tokenId,
    reason: "Revoked by developer from the developer portal",
    revokedBy: userId,
    ownedBy: userId,
  });
}
